class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      const hasGiftBox = this.closest('.cart-item').dataset.productHasGiftbox === 'true';
      const hasMonogramProduct = this.closest('.cart-item').dataset.isPersonalised === 'true';

      if (hasGiftBox || hasMonogramProduct) {
        const groupId = this.dataset.groupId;
        cartItems.removeGroupItems(groupId);
      } else {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
    // Handle click on "Remove gift box" button in the gift footer
    this.addEventListener('click', (event) => {
      const removeGiftBtn = event.target.closest('.cart-item__gift_box-remove-button-option');
      if (!removeGiftBtn) return;
      event.preventDefault();
      event.stopPropagation();

      const parentRow = removeGiftBtn.closest('.cart-item');
      const groupId = parentRow?.dataset?.groupId;
      if (!groupId) return;

      this.removeGiftBoxAndNote(groupId);
    });
    this.initProgressBar();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  /**
   * Remove the gift-box addon line(s) for a given group and clear the Note + gift properties on the main item.
   * Keeps other properties (e.g. personalisation) intact.
   * @param {string|number} groupId
   */
  async removeGiftBoxAndNote(groupId) {
    try {
      // Show loading spinners for all rows in this group
      const allCartItemEls = this.querySelectorAll(`.cart-item[data-group-id="${groupId}"]`);
      allCartItemEls.forEach((el) => {
        const lineIndex = parseInt(el.dataset.index, 10);
        if (!Number.isNaN(lineIndex)) this.enableLoading(lineIndex);
      });

      // Fetch current cart state
      const cartResp = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!cartResp.ok) throw new Error(`/cart.js returned ${cartResp.status}`);
      const cart = await cartResp.json();

      // Find addon (gift-box) lines for this group
      const addonLines = this.findAddonProducts(cart, groupId);

      // Find main product line for this group (same groupId, not an addon)
      let mainLine = null;
      cart.items.forEach((item, idx) => {
        const props = item.properties || {};
        if (String(props._groupId) === String(groupId) && !props._addon) {
          mainLine = idx + 1; // 1-based
        }
      });

      // 1) Remove gift-box addon lines first (descending so indexes don't shift)
      addonLines.sort((a, b) => b - a);
      for (const lineNum of addonLines) {
        const changeBody = JSON.stringify({ line: lineNum, quantity: 0 });
        const changeResp = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: changeBody,
        });
        if (!changeResp.ok) throw new Error(`/cart/change.js failed for line ${lineNum}: ${changeResp.status}`);
        await changeResp.json();
      }

      // 2) Re-fetch cart to account for index shifts after removals
      const refreshedResp = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!refreshedResp.ok) throw new Error(`/cart.js (refresh) returned ${refreshedResp.status}`);
      const refreshedCart = await refreshedResp.json();

      // 3) Recompute main line index after removals
      let refreshedMainLine = null;
      refreshedCart.items.forEach((item, idx) => {
        const props = item.properties || {};
        if (String(props._groupId) === String(groupId) && !props._addon) {
          refreshedMainLine = idx + 1; // 1-based
        }
      });

      // 4) Clear Note and gift-related properties on the current main line (if present)
      if (refreshedMainLine) {
        // Remove properties by setting them to null. Shopify will delete any property set to null.
        const updateBody = JSON.stringify({
          line: refreshedMainLine,
          properties: {
            Note: null,
            _hasGiftBox: null,
            _giftboxId: null,
            _giftProductHandle: null,
            _giftPid: null,
          },
        });
        const updateResp = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: updateBody,
        });
        if (!updateResp.ok) throw new Error(`/cart/change.js failed for main line ${refreshedMainLine}: ${updateResp.status}`);
        await updateResp.json();
      }

      // Re-render cart sections and refresh item count bubble/text
      this.onCartUpdate();
      if (window.updateCartItemCount) {
        try { await window.updateCartItemCount(); } catch {}
      }
    } catch (err) {
      const errorsEl = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errorsEl) {
        errorsEl.textContent = window.cartStrings.error;
      }
      console.error(err);
    } finally {
      // Disable all spinners for this group
      const allCartItemEls = this.querySelectorAll(`.cart-item[data-group-id="${groupId}"]`);
      allCartItemEls.forEach((el) => {
        const lineIndex = parseInt(el.dataset.index, 10);
        if (!Number.isNaN(lineIndex)) this.disableLoading(lineIndex);
      });
      // Always re-render to avoid stale DOM if any step failed
      this.onCartUpdate();
      if (window.updateCartItemCount) {
        try { await window.updateCartItemCount(); } catch {}
      }
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    const parent = event.target.closest('.cart-item');
    const hasGiftBox = parent.dataset.productHasGiftbox === 'true';
    const hasMonogramProduct = parent.dataset.isPersonalised === 'true';

    let groupId;

    if (hasGiftBox || hasMonogramProduct) {
      groupId = parent.dataset.groupId;
    }

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
      groupId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId, groupId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        if (!groupId) {
          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;

        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        /**
         * If a Group ID has been passed, find the addon product's cart index so
         * that that product can have it's quantity updated too.
         * - Gets the addon products with the same group ID.
         * - If there is only one addon, remove it.
         * - If there are multiple addons, call `updateQuantity` passing the
         *   `groupId` again to re-check for any remaining addon products to
         *   update.
         */
        if (groupId) {
          const linesToUpdate = this.findAddonProducts(parsedState, groupId);

          const quantityInputs = document.querySelectorAll(
            `[data-addon=true][data-group-id="${groupId}"] .quantity__input`
          );

          if (linesToUpdate.length === 1) {
            this.updateQuantity(linesToUpdate[0], quantity);
          } else if (linesToUpdate.length > 1) {
            this.updateQuantity(linesToUpdate[0], quantity, null, null, groupId);
          }

          quantityInputs.forEach((quantityInput) => {
            quantityInput.value = quantity;
          });
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        setTimeout(() => {
          this.disableLoading(line);
        }, 1000);
      });
  }

  /**
   * Remove all cart lines (and any hidden/UI‐only rows) whose properties._groupId matches `groupId`.
   * Uses /cart/change.js one‐by‐one and calls onCartUpdate() after each removal, so the drawer always stays in sync.
   *
   * @param {string|number} groupId  The group ID to match against item.properties._groupId
   */
  async removeGroupItems(groupId) {
    // 1) Show loading spinners on every <.cart-item> with this groupId
    const allCartItemEls = this.querySelectorAll('.cart-item');
    allCartItemEls.forEach(el => {
      if (String(el.dataset.groupId) === String(groupId)) {
        const lineIndex = parseInt(el.dataset.index, 10);
        this.enableLoading(lineIndex);
      }
    });

    try {
      // 2) Fetch /cart.js to see which lines (1-based) share this groupId
      const cartResp = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!cartResp.ok) throw new Error(`/cart.js returned ${cartResp.status}`);
      const cart = await cartResp.json();

      // 3) Build an array of 1-based lines to delete (where _groupId matches)
      let linesToRemove = [];
      cart.items.forEach((item, idx) => {
        if (
          item.properties &&
          String(item.properties._groupId) === String(groupId)
        ) {
          linesToRemove.push(idx + 1);
        }
      });

      // 4) If nothing matches, disable spinners and exit early
      if (linesToRemove.length === 0) {
        allCartItemEls.forEach(el => {
          const lineIndex = parseInt(el.dataset.index, 10);
          this.disableLoading(lineIndex);
        });
        return;
      }

      // 5) Sort descending so removing a higher line first doesn't shift indexes for the next lines
      linesToRemove.sort((a, b) => b - a);

      // 6) Loop through each line, remove it via /cart/change.js, then call onCartUpdate()
      for (const lineNum of linesToRemove) {
        const changeBody = JSON.stringify({ line: lineNum, quantity: 0 });

        const changeResp = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: changeBody
        });

        if (!changeResp.ok) {
          let errJson = {};
          try { errJson = await changeResp.json(); } catch {}
          throw new Error(
            `/cart/change.js failed for line ${lineNum}: ${changeResp.status} – ${JSON.stringify(errJson)}`
          );
        }

        const updatedCart = await changeResp.json();

        // 7) Publish cartUpdate so mini‐cart badges, etc. refresh immediately
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: updatedCart
        });

        // 8) Call onCartUpdate() so your drawer (or main‐cart page) replaces itself with the latest HTML
        //    This covers both scenarios: if user is in a drawer, it re-fetches `?section_id=cart-drawer`; 
        //    if on the main-cart page, it re-fetches `?section_id=main-cart-items`.
        this.onCartUpdate();

        // 9) Immediately disable the spinner on the line we just removed
        this.disableLoading(lineNum);
        
        // 10) Short pause to let the drawer re-render before removing the next line (optional, but smoother)
        await new Promise(r => setTimeout(r, 150));
      }

      // 11) After removing all lines for that group, we may still have “hidden/UI-only” rows in the DOM.
      //     Purge them now: any <.cart-item data-group-id="…"> left over (they never existed in /cart.js).
      document
        .querySelectorAll(`.cart-item[data-group-id="${groupId}"]`)
        .forEach(el => el.remove());

      // 12) Finally, fetch the cart one more time to know if it’s empty (so we can trap focus appropriately).
      const finalCartResp = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!finalCartResp.ok) throw new Error(`/cart.js returned ${finalCartResp.status} after final removals`);
      const finalCart = await finalCartResp.json();

      // 13) If the drawer still exists, toggle its “is-empty” class; otherwise skip
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.classList.toggle('is-empty', finalCart.item_count === 0);
      }

      // 14) If the main‐cart‐footer exists, toggle its “is-empty” too
      const footer = document.getElementById('main-cart-footer');
      if (footer) {
        footer.classList.toggle('is-empty', finalCart.item_count === 0);
      }

      if (window.updateCartItemCount) {
        await window.updateCartItemCount();
      }

      // 15) Trap focus to the “empty” container if cart is now empty; otherwise to first item
      if (finalCart.item_count === 0 && drawer) {
        const emptyContainer = drawer.querySelector('.drawer__inner-empty');
        const fallbackLink = drawer.querySelector('a');
        if (emptyContainer && fallbackLink) {
          trapFocus(emptyContainer, fallbackLink);
        }
      } else if (document.querySelector('.cart-item') && drawer) {
        trapFocus(drawer, document.querySelector('.cart-item__name'));
      }

      // 16) Publish one more cartUpdate so any other parts of your theme (like the mini‐cart count badge)
      //     have the final state.
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: finalCart
      });
    }
    catch (err) {
      // 17) On any error (network or API), disable all spinners and show a generic error
      allCartItemEls.forEach(el => {
        const lineIndex = parseInt(el.dataset.index, 10);
        this.disableLoading(lineIndex);
      });
      const errorsEl =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors');
      if (errorsEl) {
        errorsEl.textContent = window.cartStrings.error;
      }
    }
  }


  /**
   * Find the line numbers for addon products with a matching group id.
   * - Does this by finding a product with `_addon: true` and `_groupId`
   *   property that matches the passed `groupId`.
   * @param {Object} state - Cart state
   * @param {String|Number} groupId - Line item property value that groups main products
   * with their addon products.
   * @returns {Array<Number>}
   */
  findAddonProducts(state, groupId) {
    const addonLines = [];
    state.items.forEach((item, index) => {
      if (item.properties && item.properties._addon && parseInt(item.properties._groupId) === parseInt(groupId)) {
        const line = index + 1;
        addonLines.push(line);
      }
    });

    return addonLines;
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  initProgressBar() {
    this.fetchCartTotal()
      .then((cartTotal) => {
        if (cartTotal > 0) {
          const formattedMoney = (cartTotal / 100).toFixed(2);
          this.updateProgressBar(formattedMoney);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  fetchCartTotal() {
    return fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        let cartTotal = 0;

        cart.items.forEach((item) => {
          if (!item.gift_card) {
            cartTotal += item.line_price;
          }
        });
        return cartTotal;
      });
  }

  updateProgressBar(cartTotal) {
    const progressBar = document.getElementById('shippingProgressBar');
    const cartInfo = document.getElementById('cart-info');
    const freeShippingThreshold = parseFloat(cartInfo?.dataset?.freeShippingThreshold);

    if (freeShippingThreshold) {
      const percentage = (cartTotal / freeShippingThreshold) * 100;

      progressBar.style.width = `${percentage}%`;
      progressBar.setAttribute('aria-valuenow', cartTotal);
      progressBar.setAttribute('aria-valuemax', freeShippingThreshold);
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
